import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { FaCamera, FaTrashAlt, FaEnvelope, FaUserShield, FaIdBadge, FaLock } from 'react-icons/fa';

const MinProfil = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState('');
  const [emailDraft, setEmailDraft] = useState('');
  const [avatar, setAvatar] = useState('');
  const [message, setMessage] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [username, setUsername] = useState('');
  const [role, setRole] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const fileInputRef = useRef(null);
  const [userId, setUserId] = useState(null);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityError, setActivityError] = useState('');
  const [recentHistory, setRecentHistory] = useState([]);
  const [userLogEntries, setUserLogEntries] = useState([]);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const isAdmin = typeof role === 'string' && role.toLowerCase() === 'admin';

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      setActivityLoading(false);
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    axios.get('/api/auth/me', { headers })
      .then(res => {
        const nextEmail = res.data.email || '';
        setEmail(nextEmail);
        setEmailDraft(nextEmail);
        setAvatar(res.data.avatar || '');
        setUsername(res.data.brugernavn || '');
        setRole(res.data.rolle || '');
        setUserId(res.data.id ?? null);
        try {
          if (res.data.avatar) {
            localStorage.setItem('userAvatar', res.data.avatar);
            window.dispatchEvent(new CustomEvent('profile:avatarUpdated', { detail: { avatar: res.data.avatar } }));
          }
        } catch (_) {}
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    axios.get('/api/auth/me/activity', { headers })
      .then(res => {
        setRecentHistory(Array.isArray(res.data?.history) ? res.data.history : []);
        setUserLogEntries(Array.isArray(res.data?.log) ? res.data.log : []);
        setActivityError('');
      })
      .catch(() => setActivityError('Kunne ikke hente aktivitetsdata'))
      .finally(() => setActivityLoading(false));
  }, []);

  const handleProfileSave = async (e, nextEmailValue = email) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put('/api/auth/me', { email: nextEmailValue, avatar }, { headers: { Authorization: `Bearer ${token}` } });
      setMessage('Profil opdateret');
      setEmail(nextEmailValue);
      setEmailDraft(nextEmailValue);
      setShowEmailForm(false);
      try {
        if (avatar && avatar.startsWith('data:image/')) {
          localStorage.setItem('userAvatar', avatar);
        } else if (avatar) {
          localStorage.setItem('userAvatar', avatar);
        } else {
          localStorage.removeItem('userAvatar');
        }
        window.dispatchEvent(new CustomEvent('profile:avatarUpdated', { detail: { avatar } }));
      } catch (_) {}
    } catch (e) {
      setMessage('Kunne ikke opdatere profil');
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleAvatarFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage('Vælg venligst et billede');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    try {
      setAvatarUploading(true);
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const dataUrl = reader.result;
          const token = localStorage.getItem('token');
          const res = await axios.post('/api/auth/me/avatar', { dataUrl }, { headers: { Authorization: `Bearer ${token}` } });
          setAvatar(String(dataUrl));
          setMessage('Profilbillede opdateret');
          setTimeout(() => setMessage(''), 3000);
          try {
            window.dispatchEvent(new CustomEvent('profile:avatarUpdated', { detail: { avatar: String(dataUrl), userId: undefined } }));
          } catch (_) {}
        } catch (err) {
          setMessage(err.response?.data?.message || 'Kunne ikke uploade billede');
          setTimeout(() => setMessage(''), 3000);
        } finally {
          setAvatarUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setAvatarUploading(false);
      setMessage('Kunne ikke læse fil');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      setAvatarUploading(true);
      const token = localStorage.getItem('token');
      await axios.put('/api/auth/me', { email, avatar: null }, { headers: { Authorization: `Bearer ${token}` } });
      setAvatar('');
      setMessage('Profilbillede fjernet');
      try {
        localStorage.removeItem('userAvatar');
        window.dispatchEvent(new CustomEvent('profile:avatarUpdated', { detail: { avatar: null } }));
      } catch (_) {}
    } catch (err) {
      setMessage('Kunne ikke fjerne billede');
    } finally {
      setAvatarUploading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const triggerAvatarUpload = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      triggerAvatarUpload();
    }
  };

  const handleAvatarRemoveClick = (e) => {
    e.stopPropagation();
    if (!avatarUploading) {
      handleRemoveAvatar();
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/auth/me/password', { current_password: currentPassword, new_password: newPassword }, { headers: { Authorization: `Bearer ${token}` } });
      setMessage('Kode opdateret');
      setCurrentPassword('');
      setNewPassword('');
      setShowPasswordModal(false);
      // Efter første login – send brugeren til dashboard
      try {
        window.location.replace('/');
      } catch (_) {}
    } catch (e) {
      setMessage('Kunne ikke opdatere kode');
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      <section className="relative overflow-hidden rounded-2xl bg-white dark:bg-gray-900 shadow-xl border border-gray-100 dark:border-gray-800">
        {isAdmin && (
          <div className="pointer-events-none absolute -right-10 top-6 rotate-45 bg-falck-blue text-white text-xs font-semibold uppercase tracking-wide px-12 py-1 shadow-lg">
            Admin
          </div>
        )}
        <div className="flex flex-col items-center text-center gap-6 p-6 sm:p-8">
          <p className="inline-flex items-center gap-2 text-gray-500 dark:text-gray-400 uppercase tracking-wide text-xs">
            <FaIdBadge className="text-sm" />
            Min profil
          </p>
          <div className="relative">
            <div
              className="group relative h-32 w-32 sm:h-36 sm:w-36 rounded-full ring-2 ring-gray-200 dark:ring-gray-700 overflow-hidden shadow-lg cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-falck-blue/60"
              role="button"
              tabIndex={0}
              onClick={triggerAvatarUpload}
              onKeyDown={handleAvatarKeyDown}
              aria-label="Skift profilbillede"
            >
              <img src={avatar || '/favicon.svg'} alt="Profil" className="h-full w-full object-cover" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-xs sm:text-sm font-semibold uppercase tracking-wider text-white bg-black/60 opacity-0 group-hover:opacity-100 transition">
                <FaCamera className="mb-1 text-lg" />
                Tryk for at skifte
              </div>
              {avatarUploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                  <div className="h-10 w-10 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleAvatarRemoveClick}
              className="absolute -bottom-3 left-1/2 -translate-x-1/2 translate-y-1/2 flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-white text-red-600 rounded-full border border-gray-200 shadow-md hover:bg-gray-50 disabled:opacity-50 dark:bg-gray-800 dark:text-red-400 dark:border-gray-700"
              disabled={avatarUploading || !avatar}
            >
              <FaTrashAlt />
              Fjern
            </button>
          </div>
          <div className="space-y-3">
            <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100">
              {username || 'Min profil'}
            </h1>
          </div>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarFileChange} className="hidden" />
        <div className="border-t border-gray-100 dark:border-gray-800 px-6 sm:px-8 pb-6 sm:pb-8 pt-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Din konto</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Opdater din email-adresse og dine sikkerhedsoplysninger.
            </p>
          </div>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="h-12 w-12 rounded-2xl bg-falck-blue/10 text-falck-blue flex items-center justify-center">
                <FaEnvelope />
              </span>
              <div className="flex-1">
                <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">{email || 'Ikke angivet'}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEmailDraft(email);
                  setShowEmailForm((prev) => !prev);
                }}
                className="text-sm font-semibold text-falck-blue hover:underline"
              >
                {showEmailForm ? 'Annuller' : 'Skift'}
              </button>
            </div>
            {showEmailForm && (
              <form
                onSubmit={(e) => handleProfileSave(e, emailDraft)}
                className="space-y-4 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 p-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                  <input
                    type="email"
                    value={emailDraft}
                    onChange={(e) => setEmailDraft(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-falck-blue text-white font-semibold shadow hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Gemmer...' : 'Gem ændringer'}
                  </button>
                </div>
              </form>
            )}
            <div className="flex items-center gap-3">
              <span className="h-12 w-12 rounded-2xl bg-green-500/10 text-green-600 flex items-center justify-center">
                <FaUserShield />
              </span>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Rolle</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">{role || 'Standard'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="h-12 w-12 rounded-2xl bg-yellow-500/10 text-yellow-600 flex items-center justify-center">
                <FaLock />
              </span>
              <div className="flex-1">
                <p className="text-sm text-gray-500 dark:text-gray-400">Sikkerhed</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">Opdater kode regelmæssigt</p>
              </div>
              <button
                type="button"
                onClick={() => setShowPasswordModal(true)}
                className="text-sm font-semibold text-falck-blue hover:underline"
              >
                Skift
              </button>
            </div>
          </div>
          <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 p-4 text-sm text-gray-600 dark:text-gray-300">
            Tip: Klik direkte på dit profilbillede for hurtigt at skifte eller slette det.
          </div>
        </div>
      </section>

      {message && (
        <div className="rounded-2xl border border-emerald-100 dark:border-emerald-400/40 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-100 px-4 py-3 text-center shadow-sm">
          {message}
        </div>
      )}

        {userId === 3 && (
          <div className="grid grid-cols-1 gap-6">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6 border border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Historik</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Seneste aktivitet på tværs af services og fejlmeldinger.</p>
                </div>
                {activityLoading && (
                  <span className="text-xs uppercase tracking-wide text-gray-400">Henter...</span>
                )}
              </div>
              {activityError ? (
                <p className="text-sm text-red-500">{activityError}</p>
              ) : recentHistory.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Ingen aktivitet registreret endnu.</p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                  {recentHistory.map((item) => (
                    <li key={item.id} className="py-3 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{item.type}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-300">{item.title}</p>
                        {item.meta && <p className="text-xs text-gray-500 dark:text-gray-400">{item.meta}</p>}
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {item.date ? new Date(item.date).toLocaleString('da-DK', { dateStyle: 'short', timeStyle: 'short' }) : 'Ukendt dato'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6 border border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Brugerlog</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Hurtigt overblik over hvad andre brugere har foretaget sig.</p>
                </div>
                {activityLoading && (
                  <span className="text-xs uppercase tracking-wide text-gray-400">Henter...</span>
                )}
              </div>
              {activityError ? (
                <p className="text-sm text-red-500">{activityError}</p>
              ) : userLogEntries.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Ingen brugere har lavet noget endnu.</p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                  {userLogEntries.map((item) => (
                    <li key={item.id} className="py-3 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{item.user}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-300">{item.action}</p>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {item.date ? new Date(item.date).toLocaleString('da-DK', { dateStyle: 'short', timeStyle: 'short' }) : 'Ukendt dato'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">Skift adgangskode</h3>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Nuværende kode</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Ny kode</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  required
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Annuller
                </button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-falck-blue text-white rounded hover:bg-blue-700">
                  Gem kode
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MinProfil;
